import React, {useEffect, useRef, useState} from 'react';
import './EventListings.css';
import {Link} from 'react-router-dom';
import Listing from '../../interfaces/Listing';
import Event from '../../interfaces/Event';
import {
    CalendarWidgetFull,
    CalendarWidgetSmall,
    formatTimeZonedDateToFullDate,
    formatTimeZonedDateToWeekDayAndTime
} from '../../utils/DateUtils/DateUtils'
import {SingleSelectMenu, SliderMenu} from '../../utils/DropwdownUtils/DropdownUtils';
import {quantityMap} from './EventView';
import {getCheckoutItemUrlParams, getEventUrlParams} from '../../utils/GlobalUtils';
import ListingSource from '../../interfaces/ListingSource';
import {useScreenSizeContext} from '../../providers/ScreenSizeProvider';
import {FaChevronDown, FaChevronLeft, FaTimes} from 'react-icons/fa';
import BackToResults from '../BackToResults/BackToResults';
import FavoriteButton from '../FavoriteButton/FavoriteButton';


interface EventListingsProps {
    loadingEvent: boolean;
    loadingListings: boolean;
    event: Event | null;
    listings: Listing[];
    priceRange: [number, number];
    quantityOptions: string[];
    selectedListing: Listing | null;
    setSelectedListing: React.Dispatch<React.SetStateAction<Listing | null>>;
    selectedQuantity: string;
    setSelectedQuantity: React.Dispatch<React.SetStateAction<string>>;
    selectedPrices: [number, number];
    setSelectedPrices: React.Dispatch<React.SetStateAction<[number, number]>>;
    parkingFiltered: boolean;
    setParkingFiltered: React.Dispatch<React.SetStateAction<boolean>>;
}

const EventListings: React.FC<EventListingsProps> = ({ loadingEvent, loadingListings, event, listings, priceRange, quantityOptions, selectedListing, setSelectedListing, selectedQuantity, setSelectedQuantity, selectedPrices, setSelectedPrices, parkingFiltered, setParkingFiltered }) => {
    const quantityPickerRef = useRef<HTMLDivElement>(null);
    const pricePickerRef = useRef<HTMLDivElement>(null);

    const [selectedListingQuantity, setSelectedListingQuantity] = useState('');
    const [activeQuantityPicker, setActiveQuantityPicker] = useState(false);
    const [activePricePicker, setActivePricePicker] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    const quantityFiltered = selectedQuantity !== '';
    const priceFiltered = selectedPrices[0] !== priceRange[0] || selectedPrices[1] !== priceRange[1];

    const { isMobile, isMapMobile } = useScreenSizeContext();


    const extractQuantityNumber = (quantityString: string) => {
        const regex = /^(\d+) Tickets?$/;
        const match = quantityString.match(regex)

        if (match && match[1]) {
            return parseInt(match[1], 10);
        }
        return 1
    }


    useEffect(() => {
        const handleClickOutsideQuantityPicker = (e: MouseEvent) => {
            if (quantityPickerRef.current && e.target instanceof Node && !quantityPickerRef.current.contains(e.target)) {
                setActiveQuantityPicker(false);
            }
        };

        const handleClickOutsidePricePicker = (e: MouseEvent) => {
            if (pricePickerRef.current && e.target instanceof Node && !pricePickerRef.current.contains(e.target)) {
                setActivePricePicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutsideQuantityPicker);
        document.addEventListener('mousedown', handleClickOutsidePricePicker);

        return () => {
            document.removeEventListener('mousedown', handleClickOutsideQuantityPicker);
            document.removeEventListener('mousedown', handleClickOutsidePricePicker);
        };
    }, []);

    const toPriceString = (value: number) => {
        const formattedPrice = new Intl.NumberFormat().format(Math.floor(value));
        const decimalPart = (value % 1).toFixed(2).substring(1);
        return `$${formattedPrice}${decimalPart}`;
    };


    useEffect(() => {
        if (selectedQuantity !== '') {
            const quantity = quantityMap.get(selectedQuantity);
            setSelectedListingQuantity(quantity + (quantity == 1 ? " Ticket" : " Tickets"));
        } else {
            setSelectedListingQuantity('');
        }
        setActiveQuantityPicker(false);
    }, [selectedQuantity]);


    const handleSelectedListing = (listing: Listing) => {
        if (selectedListingQuantity == '') {
            const minSplit = Math.min(...listing.splits)
            setSelectedListingQuantity(minSplit + (minSplit == 1 ? " Ticket" : " Tickets"));
        }
        setSelectedListing(listing)
    }

    const handleResetFilters = () => {
        setSelectedQuantity('');
        setSelectedPrices(priceRange);
        setParkingFiltered(false);
        setShowFilters(false);
    }

    const handleBack = () => {
        setSelectedListing(null)
        setSelectedListingQuantity('');
    }


    return (
        selectedListing !== null ?
            <div className='event-listings-container'>
                {(loadingEvent || event == null) ?
                    <div className="loading-spinner"></div>
                    :
                    <>
                        <div className='pad border-bottom' style={{backgroundColor: 'white'}}>
                            <div className='card-item'>
                                <div className='row'>
                                    <div className='left'>
                                        <FaChevronLeft className='back-icon' onClick={handleBack} />
                                        <div className={`${isMobile ? "text-s-bold" : "text-m"}`}>Section {selectedListing.section} • Row {selectedListing.row}</div>
                                    </div>
                                    <div className={`${isMobile ? "text-s-bold" : "text-m"}`}>
                                        {toPriceString(selectedListing.price)}&nbsp;
                                    </div>
                                    <div className={`${isMobile ? "text-xs-light" : "text-s-light"}`}>
                                        ea
                                    </div>
                                </div>
                            </div>
                            <div className='card-item'>
                                <div className='row'>
                                    <div className='left'>
                                        <div className='relative' ref={quantityPickerRef}>
                                            <button className='filter-button' onClick={(() => setActiveQuantityPicker(!activeQuantityPicker))}>{selectedListingQuantity !== '' ? selectedListingQuantity : "Tickets"}
                                                <FaChevronDown className="carrot-icon" style={{
                                                    transform: (activeQuantityPicker) ? 'rotate(180deg)' : 'rotate(0deg)',
                                                    transition: '200ms ease-in-out'
                                                }} />
                                            </button>
                                            {activeQuantityPicker &&
                                                <SingleSelectMenu
                                                    options={selectedListing.splits.map(qty => qty + (qty == 1 ? " Ticket" : " Tickets"))}
                                                    selectedOption={selectedListingQuantity}
                                                    setSelectedOption={setSelectedListingQuantity}
                                                    closeMenu={() => setActiveQuantityPicker(false)}
                                                    mustSelect={true}
                                                />
                                            }
                                        </div>
                                        <div className='gap-s' />
                                        <Link
                                            to={`/checkout/${getCheckoutItemUrlParams({ eventId: event.tevoEventId, listingId: selectedListing.listingId as number, selectedQuantity: extractQuantityNumber(selectedListingQuantity), listingSource: selectedListing.listingSource })}`}
                                            className='action-button'
                                        >
                                            Checkout
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='card-item'>
                            <div className='row'>
                                <div className='left'>
                                    <div className='listing-info-block'>
                                        <div className='item-title'>{event?.name}</div>
                                        <div className='text-xs-light'>{event ? formatTimeZonedDateToFullDate(event?.occursAtLocal) : ''}</div>
                                    </div>
                                </div>
                            </div>
                            <div className='row'>
                                <div className='left'>
                                    <div className='listing-info-block'>
                                        <div className='item-title'>{event?.venue.name}</div>
                                        <div className='text-xs-light'>{event?.venue.location}</div>
                                    </div>
                                </div>
                            </div>
                            <hr className='divider'></hr>
                            <div className='row'>
                                <div className='left'>
                                    <div className='listing-info-block'>
                                        <div className='item-title'>100% Buyer Guarantee</div>
                                        <div className='text-xs-light'>The 714tickets Guarantee means tickets will be 100% authentic and delivered on time, each and every time. Price is the same in store as it is online, all without service fees.</div>
                                    </div>
                                </div>
                            </div>
                            <hr className='divider'></hr>
                            <div className='row'>
                                <div className='left'>
                                    <div className='listing-info-block'>
                                        <div className='item-title'>Notes</div>
                                        <div className='text-xs-light'>{selectedListing.publicNotes}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                }
            </div>
            : // Selected Listing code above, Listings List code below
            <div className='event-listings-container'>
                <div className='pad border-bottom sticky-listings-header'>
                    <BackToResults />
                    <div className='card-item'>
                        {(loadingEvent || event == null) ?
                            <div className="loading-spinner"></div>
                            :
                            <div className='row'>
                                <div className='left'>
                                    <div className='col align-start'>
                                        {isMobile ?
                                            <>
                                                <div className='text-s-bold'>{event.name}</div>
                                                <div className="text-xxs venue-favorite-row">
                                                    {event.venue.location + ' - ' + event.venue.name + ' - ' + formatTimeZonedDateToWeekDayAndTime(event.occursAtLocal)}
                                                    <FavoriteButton
                                                        type="venue"
                                                        tevoId={event.venue.tevoVenueId}
                                                        name={event.venue.name}
                                                    />
                                                </div>
                                            </>
                                            :
                                            <>
                                                <div className='text-m'>{event.name}</div>
                                                <div className='text-s-light venue-favorite-row'>
                                                    {event.venue.name + ' | ' + event.venue.location}
                                                    <FavoriteButton
                                                        type="venue"
                                                        tevoId={event.venue.tevoVenueId}
                                                        name={event.venue.name}
                                                    />
                                                </div>
                                            </>
                                        }

                                    </div>
                                    <div className='gap-m' />

                                </div>
                                {isMobile &&
                                    <div style={{ marginRight: "-20px" }}>
                                        <CalendarWidgetSmall dateTime={event.occursAtLocal} />
                                    </div>}
                                {!isMobile && <CalendarWidgetFull dateTime={event.occursAtLocal} />}

                            </div>
                        }
                    </div>
                    <div className={`${!isMapMobile && 'card-item'}`}>
                        {(!loadingListings && event != null) && isMapMobile && (
                            <>
                                <div className='row'>
                                    <button className={`filter-button`} onClick={(() => setShowFilters(!showFilters))}>{showFilters ? "Hide Filters" : "Filter Listings"}</button>
                                    <div className='gap-s' />
                                    {event.listingSource != ListingSource.CUSTOM &&
                                        <Link to={`/consignment/${getEventUrlParams(event)}`}
                                            className='action-button'
                                        >
                                            Sell Tickets
                                        </Link>}
                                </div>
                                {showFilters &&
                                    <div className='break-s' />
                                }
                            </>
                        )
                        }
                        {!loadingListings && (!isMapMobile || showFilters) &&

                            <div className={`${isMobile ? "col" : "row"} `}>
                                <div className={`${isMobile ? "row" : 'row'} `}>

                                    <div className='relative' ref={quantityPickerRef} >
                                        <button className={`filter-button ${quantityFiltered && "filtered"}`} onClick={(() => setActiveQuantityPicker(!activeQuantityPicker))}>{quantityFiltered ? selectedQuantity : "Quantity"}

                                            <FaChevronDown className={`${quantityFiltered ? "carrot-icon-selected" : "carrot-icon"}`} style={{
                                                transform: (activeQuantityPicker) ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: '200ms ease-in-out'
                                            }} />
                                        </button>

                                        {activeQuantityPicker &&
                                            <SingleSelectMenu
                                                options={quantityOptions}
                                                selectedOption={selectedQuantity}
                                                setSelectedOption={setSelectedQuantity}
                                                closeMenu={() => setActiveQuantityPicker(false)}
                                                mustSelect={false}
                                            />
                                        }
                                    </div>
                                    {quantityFiltered && quantityMap.get(selectedQuantity)! >= 2 &&
                                        <div className='text-xxs' style={{color: '#278dc7', marginLeft: '8px', whiteSpace: 'nowrap'}}>Seats guaranteed together</div>
                                    }
                                    <div className='gap-s' />
                                    <div className='relative' ref={pricePickerRef} >
                                        <button className={`filter-button ${priceFiltered && "filtered"}`} onClick={(() => setActivePricePicker(!activePricePicker))}>{(priceFiltered && !isMobile) ? "$" + new Intl.NumberFormat().format(selectedPrices[0]) + " - $" + new Intl.NumberFormat().format(selectedPrices[1]) : "Price"}</button>
                                        {activePricePicker &&
                                            <div style={{marginLeft: isMobile ? '-100px' : '0px'}}>
                                            <SliderMenu
                                                rangeOptions={priceRange}
                                                selectedOptions={selectedPrices}
                                                setSelectedOptions={setSelectedPrices}
                                                onClose={() => setActivePricePicker(false)}
                                            />
                                            </div>
                                        }
                                    </div>

                                    <div className='gap-s' />
                                    <button className={`filter-button ${parkingFiltered && "filtered"}`} onClick={() => setParkingFiltered(!parkingFiltered)}>Parking</button>
                                    {((quantityFiltered || priceFiltered || parkingFiltered) && !isMobile) &&
                                        <>
                                            <br />
                                            <div className='gap-s' />
                                            <button className='filter-button' onClick={handleResetFilters} style={isMobile ? { width: '100%', margin: '0px 20px' } : {}}>
                                                <div className='col'>
                                                    <FaTimes />
                                                </div>
                                            </button>
                                        </>
                                    }
                                </div>
                                {((quantityFiltered || priceFiltered || parkingFiltered) && isMobile) &&
                                    <>
                                        <div className='break-s' />
                                        <div className='gap-s' />
                                        <button className='filter-button' onClick={handleResetFilters} style={isMobile ? { width: '100%', margin: '0px 20px' } : {}}>
                                            Clear Filters
                                        </button>
                                    </>
                                }
                            </div>
                        }
                    </div>
                </div>
                <div className='event-listings-list'>
                    {loadingListings || event == null ? (
                        <div className="loading-spinner"></div>
                    ) : (
                        <>
                            <div className='row pad-v'>
                                <div className='left'>
                                    <div className={`${isMobile ? "item-title" : "text-s-bold"}`}>&nbsp;No Fees, One Price</div>
                                </div>
                                {event.listingSource != ListingSource.CUSTOM && !isMapMobile &&
                                    <Link to={`/consignment/${getEventUrlParams(event)}`}
                                        className='action-button'
                                    >
                                        Sell Tickets
                                    </Link>}
                            </div>
                            {listings
                                .map(listing => ({
                                    listing,
                                    // SOF-104: drop 0 from splits so a listing with splits=[0,1,2]
                                    // renders "1-2 Tickets" instead of "0-2 Tickets".
                                    validSplits: Array.isArray(listing.splits) ? listing.splits.filter(s => s > 0) : [],
                                }))
                                .filter(({ listing, validSplits }) => listing.qty > 0 && validSplits.length > 0)
                                .map(({ listing, validSplits }) => {

                                const minSplit = Math.min(...validSplits);
                                const maxSplit = Math.max(...validSplits);
                                const numTickets = maxSplit == 1 ? "1 Ticket" : (minSplit !== maxSplit ? minSplit + "-" + maxSplit : minSplit) + " Tickets"

                                return (
                                    <div className='listing-row' key={listing.listingId} onClick={() => handleSelectedListing(listing)}>
                                        <div className='left'>
                                            <div className='col align-start'>
                                                <div className='text-xs-bold'>
                                                    Section {listing.section} • Row {listing.row}&nbsp;
                                                </div>
                                                <div className={`${isMobile ? "text-xxs" : "text-xs-light"}`}>
                                                    {numTickets} | Mobile Delivery
                                                </div>
                                            </div>
                                        </div>
                                        <div className='text-xs-bold'>
                                            {toPriceString(listing.price)}&nbsp;
                                        </div>
                                        <div className={`${isMobile ? "text-xxs" : "text-xs-light"}`}>
                                            ea
                                        </div>
                                    </div>
                                )
                            }
                            )}
                        </>
                    )}
                </div>
            </div>
    )
}

export default EventListings;
